import { AnimatePresence, motion } from 'framer-motion';
import jsPDF from 'jspdf';
import { Calendar, Download, MapPin, Search } from 'lucide-react';
import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import { getAllSites } from '../SiteManagement/siteApi';

import userService from '../../services/userService';
import { fdocApi } from '../Tax/FdocApi';
import { Card } from '../ui';
import { quotationApi } from './quotationApi';

const getCurrentMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

// Helper function to map API quotation to UI format
const mapQuotationToUI = (apiQuotation) => {
  return {
    id: apiQuotation.quote_id,
    quotationNumber: apiQuotation.quotation_number || apiQuotation.quote_number || 'N/A',
    date: apiQuotation.issued_date || apiQuotation.quotation_date || apiQuotation.created_at || new Date().toISOString().split('T')[0],
    client: apiQuotation.client_name || apiQuotation.customer_name || 'Unknown Customer',
    customerId: apiQuotation.customer_id || apiQuotation.client_id,
    siteId: apiQuotation.site_id || null,
    siteName: apiQuotation.site_name || 'N/A',
    expiryDate: apiQuotation.expiry_date || 'N/A',
    lineItems: ((apiQuotation.line_items || apiQuotation.items || [])).map(item => ({
      name: item.description || item.item_name || item.name || '',
      hsnSacCode: item.hsnSacCode ?? item.hsn_code ?? item.hsnCode ?? item.sac_code ?? item.sacCode ?? '',
      sacCode: item.sac_code ?? item.sacCode ?? item.hsn_code ?? item.hsnSacCode ?? '',
      quantity: item.quantity ?? item.quantity_value ?? 0,
      units: item.unit ?? item.quantity_units ?? '',
      rate: item.unit_price || item.rate || 0,
      amount: item.line_total || item.amount || 0,
    })),
    subtotal: apiQuotation.subtotal || 0,
    discount: apiQuotation.discount_amount || 0,
    shippingHandling: apiQuotation.shipping_handling || apiQuotation.shipping_total || 0,
    insurance: apiQuotation.insurance || 0,
    totalTax: apiQuotation.tax_total || 0,
    grandTotal: apiQuotation.grand_total || apiQuotation.total || 0,
    status: apiQuotation.status || 'Draft',
    currency: apiQuotation.currency || 'INR',
    notes: apiQuotation.notes || '',
    // Preserve FDOC reference if present
    fdoc_id: apiQuotation.fdoc_id,
    fdoc_document: apiQuotation.fdoc_document,
    version_no: apiQuotation.version_no ?? apiQuotation.versionNo ?? null,
    versionNo: apiQuotation.version_no ?? apiQuotation.versionNo ?? null,
  };
};

const resolveSiteValue = (site) => {
  return site?.site_id ?? site?.siteId ?? site?.id ?? site?._id ?? site?.site_code ?? site?.siteCode ?? '';
};

const QuotationView = ({ 
  externalSite = null, 
  externalMonth = null, 
  externalSites = null,
  hideSelectors = false,
  fetchFromFdoc = false,
  esignedOnly = false,
}) => {
  // Log screen view
  useScreenLogging('QuotationView', {
    component: 'QuotationView',
    screenType: 'quotation-view',
  });

  const location = useLocation();
  const [selectedSite, setSelectedSite] = useState(externalSite || null);
  const [selectedMonth, setSelectedMonth] = useState(externalMonth || getCurrentMonth());
  const [selectedCard, setSelectedCard] = useState(null);
  const [sites, setSites] = useState(externalSites || []);
  const [isSitesLoading, setIsSitesLoading] = useState(false);
  const [sitesError, setSitesError] = useState('');
  const [quotations, setQuotations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [quotationDetails, setQuotationDetails] = useState({}); // Cache for full quotation details
  const [quotationNumberFilter, setQuotationNumberFilter] = useState('');
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [downloadUrlExpiresIn, setDownloadUrlExpiresIn] = useState(null);
  const [documentContentType, setDocumentContentType] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  // Update internal state when external props change
  useEffect(() => {
    if (externalSite !== null) {
      setSelectedSite(externalSite);
    }
  }, [externalSite]);

  useEffect(() => {
    if (externalMonth !== null) {
      setSelectedMonth(externalMonth);
    }
  }, [externalMonth]);

  useEffect(() => {
    if (externalSites !== null) {
      setSites(externalSites);
    }
  }, [externalSites]);

  // Fetch sites only if not provided externally
  useEffect(() => {
    if (externalSites !== null) {
      // Sites are provided externally, don't fetch
      return;
    }

    let isMounted = true;
    const fetchSites = async () => {
      setIsSitesLoading(true);
      setSitesError('');
      try {
        const response = await getAllSites();
        const list = Array.isArray(response?.sites)
          ? response.sites
          : Array.isArray(response)
            ? response
            : [];
        const sorted = [...list].sort((a, b) =>
          (a?.site_name || a?.name || '').localeCompare(b?.site_name || b?.name || '')
        );
        if (isMounted) {
          setSites(sorted);
        }
      } catch (error) {
        if (isMounted) {
          setSitesError(error?.message || 'Unable to load sites. Please try again later.');
        }
      } finally {
        if (isMounted) {
          setIsSitesLoading(false);
        }
      }
    };
    fetchSites();
    return () => {
      isMounted = false;
    };
  }, [externalSites]);

  // Fetch quotations when site and month change
  useEffect(() => {
    if (!selectedSite) {
      setQuotations([]);
      return;
    }

    let isMounted = true;

    const fetchQuotations = async () => {
      setIsLoading(true);
      setError('');

      try {
        const [year, month] = selectedMonth.split('-');
        const startDate = `${year}-${month}-01`;
        const lastDay = new Date(parseInt(year), parseInt(month), 0).getDate();
        const endDate = `${year}-${month}-${String(lastDay).padStart(2, '0')}`;

        const siteId = resolveSiteValue(selectedSite);

        // If fetchFromFdoc is true, fetch from FDOC API instead of quotation API
        if (fetchFromFdoc) {
          console.log('QuotationView: Fetching quotations from FDOC (Estimation OO)');
          const fdocParams = { limit: 500 };
          if (esignedOnly) {
            fdocParams.esign_status = 'applied';
          }
          const fdocResponse = await fdocApi.listDocuments(fdocParams);
          
          // Process FDOC documents to find Quotation/Estimation types
          const fdocBatchSize = 10;
          const fdocQuotations = [];
          
          for (let i = 0; i < fdocResponse.items.length; i += fdocBatchSize) {
            const batch = fdocResponse.items.slice(i, i + fdocBatchSize);
            const batchResults = await Promise.allSettled(
              batch.map(async (doc) => {
                try {
                  const fullDoc = await fdocApi.getDocument(doc.fdoc_id);
                  const extractedData = fullDoc.extracted_data;
                  let docType = extractedData?.type || 'UNKNOWN';
                  
                  // Infer type from file name if needed
                  if (docType === 'UNKNOWN' || !docType) {
                    const fileName = (fullDoc?.file_name || doc?.file_name || '').toLowerCase();
                    const fileKey = (fullDoc?.s3_key || doc?.s3_key || '').toLowerCase();
                    const combinedText = `${fileName} ${fileKey}`;
                    
                    if (combinedText.includes('quotation') || combinedText.includes('quote') || combinedText.includes('estimation') || combinedText.includes('estimate')) {
                      docType = 'Quotation';
                    }
                  }
                  
                  // Normalize Quotation types
                  const normalizedType = docType.toLowerCase();
                  if (normalizedType === 'quotation' || normalizedType === 'quote' || normalizedType === 'estimation' || normalizedType === 'estimate') {
                    // Get site_id from document
                    const docSiteId = fullDoc?.site_id || doc.site_id || fullDoc?.siteId || doc.siteId || null;
                    
                    // Check site_id match if site filter is applied
                    if (siteId) {
                      if (!docSiteId || docSiteId !== siteId) {
                        return null; // Skip if site doesn't match
                      }
                    }
                    
                    // Check date range if provided
                    const docDate = doc.uploaded_at ? new Date(doc.uploaded_at).toISOString().split('T')[0] : null;
                    if (docDate) {
                      if (startDate && docDate < startDate) {
                        return null;
                      }
                      if (endDate && docDate > endDate) {
                        return null;
                      }
                    }
                    
                    // Transform FDOC document to quotation format
                    const extractedFields = extractedData?.fields || {};
                    const textBlocks = extractedFields.text_blocks || [];
                    const rawText = extractedFields.raw_text || '';
                    
                    // Extract quotation number
                    let quotationNumber = extractedFields.quotation_number || extractedFields.quote_number || extractedFields.quotationNumber;
                    if (!quotationNumber && textBlocks.length > 0) {
                      const quoteMatch = textBlocks.find(block => 
                        typeof block === 'string' && /quote|quotation/i.test(block) && /\d+/.test(block)
                      );
                      if (quoteMatch) {
                        const numMatch = quoteMatch.match(/(\d+)/);
                        if (numMatch) {
                          quotationNumber = `QUOTE-${numMatch[1]}`;
                        }
                      }
                    }
                    if (!quotationNumber) {
                      quotationNumber = `FDOC-${doc.fdoc_id.substring(0, 8).toUpperCase()}`;
                    }
                    
                    // Extract quotation date
                    let quotationDate = extractedFields.quotation_date || extractedFields.issued_date || extractedFields.date;
                    if (!quotationDate && textBlocks.length > 0) {
                      const dateMatch = textBlocks.find(block => 
                        typeof block === 'string' && /date.*\d{1,2}\s+\w+\s+\d{4}/i.test(block)
                      );
                      if (dateMatch) {
                        const dateMatchResult = dateMatch.match(/(\d{1,2}\s+\w+\s+\d{4})/i);
                        if (dateMatchResult) {
                          try {
                            quotationDate = new Date(dateMatchResult[1]).toISOString().split('T')[0];
                          } catch (e) {
                            // If date parsing fails, use docDate
                          }
                        }
                      }
                    }
                    if (!quotationDate) {
                      quotationDate = docDate || new Date().toISOString().split('T')[0];
                    }
                    
                    // Extract expiry date
                    let expiryDate = extractedFields.expiry_date || extractedFields.valid_until || '';
                    if (!expiryDate && textBlocks.length > 0) {
                      const expiryIndex = textBlocks.findIndex(block => 
                        typeof block === 'string' && /expiry|valid|until/i.test(block)
                      );
                      if (expiryIndex >= 0 && expiryIndex + 1 < textBlocks.length) {
                        const expiryValue = textBlocks[expiryIndex + 1];
                        if (typeof expiryValue === 'string') {
                          const dateMatch = expiryValue.match(/(\d{1,2}\s+\w+\s+\d{4})/i);
                          if (dateMatch) {
                            try {
                              expiryDate = new Date(dateMatch[1]).toISOString().split('T')[0];
                            } catch (e) {
                              // Continue
                            }
                          }
                        }
                      }
                    }
                    
                    // Look up site name from sites list if available
                    let siteName = 'N/A';
                    const sitesList = externalSites && externalSites.length > 0 ? externalSites : sites;
                    if (docSiteId && sitesList.length > 0) {
                      const site = sitesList.find(s => resolveSiteValue(s) === docSiteId);
                      if (site) {
                        siteName = site.site_name || site.name || 'N/A';
                      }
                    }
                    if (siteName === 'N/A' && fullDoc?.site_name) {
                      siteName = fullDoc.site_name;
                    }
                    
                    // Extract client/customer name - try to get from customer API if customer_id exists
                    let clientName = extractedFields.client_name || extractedFields.customer_name || extractedFields.customerName || 'Unknown Customer';
                    
                    // Extract amounts
                    let subtotal = parseFloat(extractedFields.subtotal || extractedFields.sub_total || 0);
                    let discount = parseFloat(extractedFields.discount || extractedFields.discount_amount || 0);
                    let shippingHandling = parseFloat(extractedFields.shipping_handling || extractedFields.shipping_total || 0);
                    let insurance = parseFloat(extractedFields.insurance || 0);
                    let totalTax = parseFloat(extractedFields.tax_total || extractedFields.tax_amount || 0);
                    let grandTotal = parseFloat(extractedFields.grand_total || extractedFields.total_amount || extractedFields.total || 0);
                    
                    // Extract from text_blocks if amounts are 0
                    if (textBlocks.length > 0 && (grandTotal === 0 || subtotal === 0)) {
                      // Extract grand total
                      const grandTotalIndex = textBlocks.findIndex(block => 
                        typeof block === 'string' && /grand\s+total/i.test(block)
                      );
                      if (grandTotalIndex >= 0) {
                        for (let offset = 1; offset <= 3 && grandTotalIndex + offset < textBlocks.length; offset++) {
                          const totalValue = textBlocks[grandTotalIndex + offset];
                          if (typeof totalValue === 'string') {
                            const cleanedTotal = totalValue.replace(/[₹,\s]/g, '');
                            const parsedTotal = parseFloat(cleanedTotal);
                            if (!isNaN(parsedTotal) && parsedTotal > 0) {
                              grandTotal = parsedTotal;
                              break;
                            }
                          }
                        }
                      }
                      
                      // Extract subtotal
                      const subtotalIndex = textBlocks.findIndex(block => 
                        typeof block === 'string' && /sub\s*total/i.test(block)
                      );
                      if (subtotalIndex >= 0 && subtotalIndex + 1 < textBlocks.length && subtotal === 0) {
                        const subtotalValue = textBlocks[subtotalIndex + 1];
                        if (typeof subtotalValue === 'string') {
                          const cleanedSubtotal = subtotalValue.replace(/[₹,\s]/g, '');
                          const parsedSubtotal = parseFloat(cleanedSubtotal);
                          if (!isNaN(parsedSubtotal) && parsedSubtotal > 0) {
                            subtotal = parsedSubtotal;
                          }
                        }
                      }
                    }
                    
                    // Extract line items from text_blocks
                    let lineItems = extractedFields.line_items || extractedFields.items || [];
                    
                    // If no line items, try to extract from text_blocks table format
                    if (lineItems.length === 0 && textBlocks.length > 0) {
                      // Look for table header row
                      let headerStartIndex = -1;
                      for (let i = 0; i < textBlocks.length - 6; i++) {
                        const blocks = textBlocks.slice(i, i + 7);
                        const blockStrings = blocks.filter(b => typeof b === 'string').map(b => b.toUpperCase());
                        
                        let matches = 0;
                        if (blockStrings[0] && (blockStrings[0].includes('S.NO') || blockStrings[0].includes('SNO'))) matches++;
                        if (blockStrings[1] && blockStrings[1].includes('NAME')) matches++;
                        if (blockStrings[2] && (blockStrings[2].includes('HSN') || blockStrings[2].includes('CODE'))) matches++;
                        if (blockStrings[3] && blockStrings[3].includes('UNITS')) matches++;
                        if (blockStrings[4] && blockStrings[4].includes('RATE')) matches++;
                        if (blockStrings[5] && blockStrings[5].includes('TAX')) matches++;
                        if (blockStrings[6] && blockStrings[6].includes('AMOUNT')) matches++;
                        
                        if (matches >= 5) {
                          headerStartIndex = i;
                          break;
                        }
                      }
                      
                      if (headerStartIndex >= 0) {
                        let itemStartIndex = headerStartIndex + 7;
                        for (let i = headerStartIndex + 7; i < textBlocks.length; i++) {
                          const block = textBlocks[i];
                          if (typeof block === 'string' && /^\d+$/.test(block.trim())) {
                            itemStartIndex = i;
                            break;
                          }
                        }
                        
                        let currentIndex = itemStartIndex;
                        while (currentIndex < textBlocks.length) {
                          const currentBlock = textBlocks[currentIndex];
                          if (typeof currentBlock === 'string') {
                            const upperBlock = currentBlock.toUpperCase();
                            if (upperBlock.includes('SUBTOTAL') || upperBlock.includes('DISCOUNT') || upperBlock.includes('GRAND TOTAL') || upperBlock.includes('PAGE')) {
                              break;
                            }
                          }
                          
                          if (currentIndex + 6 < textBlocks.length) {
                            const sno = textBlocks[currentIndex];
                            const name = textBlocks[currentIndex + 1];
                            const hsn = textBlocks[currentIndex + 2];
                            const units = textBlocks[currentIndex + 3];
                            const rate = textBlocks[currentIndex + 4];
                            const tax = textBlocks[currentIndex + 5];
                            const amount = textBlocks[currentIndex + 6];
                            
                            if (typeof sno === 'string' && /^\d+$/.test(sno.trim()) &&
                                typeof name === 'string' && name.trim().length > 0 &&
                                typeof units === 'string' && /^\d+(?:\.\d+)?$/.test(units.trim())) {
                              
                              const quantity = parseFloat(units) || 0;
                              const unitPrice = parseFloat(rate) || 0;
                              const amountStr = typeof amount === 'string' ? amount.replace(/[₹,\s]/g, '') : '0';
                              const lineTotal = parseFloat(amountStr) || 0;
                              
                              let taxRate = 0;
                              if (typeof tax === 'string') {
                                const taxMatch = tax.match(/(\d+(?:\.\d+)?)/);
                                if (taxMatch) {
                                  taxRate = parseFloat(taxMatch[1]) || 0;
                                }
                              }
                              
                              lineItems.push({
                                description: name.trim(),
                                item_name: name.trim(),
                                quantity: quantity,
                                unit: 'pcs',
                                unit_price: unitPrice,
                                rate: unitPrice,
                                line_total: lineTotal,
                                tax_rate: taxRate,
                                hsn_code: typeof hsn === 'string' ? hsn.trim() : '',
                              });
                              
                              currentIndex += 7;
                            } else {
                              currentIndex++;
                            }
                          } else {
                            break;
                          }
                        }
                      }
                    }
                    
                    // Calculate subtotal from line items if not set
                    if (subtotal === 0 && lineItems.length > 0) {
                      subtotal = lineItems.reduce((sum, item) => {
                        return sum + (parseFloat(item.line_total) || parseFloat(item.amount) || 0);
                      }, 0);
                    }
                    
                    return {
                      quote_id: doc.fdoc_id,
                      quotation_number: quotationNumber,
                      issued_date: quotationDate,
                      quotation_date: quotationDate,
                      client_name: clientName,
                      customer_name: clientName,
                      site_id: docSiteId,
                      site_name: siteName,
                      expiry_date: expiryDate,
                      line_items: lineItems,
                      items: lineItems,
                      subtotal: subtotal,
                      discount_amount: discount,
                      shipping_handling: shippingHandling,
                      shipping_total: shippingHandling,
                      insurance: insurance,
                      tax_total: totalTax,
                      grand_total: grandTotal,
                      total: grandTotal,
                      status: 'Draft',
                      currency: 'INR',
                      notes: '',
                      version_no:
                        fullDoc?.version_no ??
                        fullDoc?.versionNo ??
                        doc?.version_no ??
                        doc?.versionNo ??
                        null,
                      // Store FDOC reference
                      fdoc_id: doc.fdoc_id,
                      fdoc_document: fullDoc,
                    };
                  }
                  return null;
                } catch (err) {
                  console.warn(`QuotationView: Failed to process FDOC document ${doc.fdoc_id}:`, err.message);
                  return null;
                }
              })
            );
            
            batchResults.forEach((result) => {
              if (result.status === 'fulfilled' && result.value) {
                fdocQuotations.push(result.value);
              }
            });
          }
          
          // Map FDOC quotations to UI format
          const mappedQuotations = fdocQuotations.map(quote => {
            const mapped = mapQuotationToUI(quote);
            const v = quote.version_no ?? quote.versionNo ?? null;
            mapped.version_no = v;
            mapped.versionNo = v;
            return mapped;
          });
          
          if (isMounted) {
            setQuotations(mappedQuotations);
          }
          return;
        }

        // Regular quotation API fetch
        const params = {
          limit: 100,
        };

        // Add quotation number filter if provided
        if (quotationNumberFilter.trim()) {
          params.quotation_number_contains = quotationNumberFilter.trim();
        }

        const result = await quotationApi.listQuotations(params);

        if (result.success) {
          // Filter by site and date range
          let filtered = (result.items || []).filter(quote => {
            const quoteDate = quote.issued_date || quote.quotation_date || quote.created_at;
            if (quoteDate) {
              const quoteDateStr = new Date(quoteDate).toISOString().split('T')[0];
              return quoteDateStr >= startDate && quoteDateStr <= endDate;
            }
            return false;
          });

          // Filter by site if site_id is available
          if (siteId) {
            filtered = filtered.filter(quote => {
              const quoteSiteId = quote.site_id || '';
              return quoteSiteId === siteId || resolveSiteValue({ site_id: quoteSiteId }) === siteId;
            });
          }

          const mappedQuotations = filtered.map(quote => {
            return mapQuotationToUI(quote);
          });

          if (isMounted) {
            setQuotations(mappedQuotations);
          }
        } else {
          if (isMounted) {
            setError(result.error || 'Failed to fetch quotations');
            setQuotations([]);
          }
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message || 'Failed to fetch quotations');
          setQuotations([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchQuotations();

    return () => {
      isMounted = false;
    };
  }, [selectedSite, selectedMonth, fetchFromFdoc, externalSites, sites, quotationNumberFilter, esignedOnly]);

  // Fetch full quotation details when a card is selected
  useEffect(() => {
    if (!selectedCard || !selectedCard.id) return;

    // Reset document states when card changes
    setDownloadUrl(null);
    setDownloadUrlExpiresIn(null);
    setDocumentContentType(null);

    // Check if this is an FDOC document (OO estimation)
    if (selectedCard.fdoc_id || fetchFromFdoc) {
      setIsLoadingDetails(true);
      const fetchFdocDocument = async () => {
        try {
          const fdocId = selectedCard.fdoc_id;
          if (!fdocId) {
            setIsLoadingDetails(false);
            return;
          }

          const fullDoc = await fdocApi.getDocument(fdocId);
          console.log('FDOC Document fetched:', fullDoc);

          // Try to find a direct download URL first
          const downloadUrlFromDoc = fullDoc?.download_url || fullDoc?.file_url || fullDoc?.s3_url || fullDoc?.presigned_url || fullDoc?.view_url;
          if (downloadUrlFromDoc && (downloadUrlFromDoc.startsWith('http://') || downloadUrlFromDoc.startsWith('https://'))) {
            setDownloadUrl(downloadUrlFromDoc);
            setDocumentContentType(fullDoc?.content_type || fullDoc?.file_content_type || 'application/pdf');
          } else {
            // Get S3 key from document - try keys in priority order
            // Build list of available keys in priority order
            const availableKeys = [
              fullDoc?.s3_key_permanent,
              fullDoc?.s3_key,
              fullDoc?.s3_key_temp,
              fullDoc?.object_key
            ].filter(key => key && key.trim() !== '');
            
            console.log('QuotationView: Available S3 keys:', {
              s3_key_permanent: fullDoc?.s3_key_permanent,
              s3_key: fullDoc?.s3_key,
              s3_key_temp: fullDoc?.s3_key_temp,
              object_key: fullDoc?.object_key,
              availableKeys
            });
            
            if (availableKeys.length === 0) {
              console.warn('QuotationView: No S3 key or download URL found in FDOC document');
              return;
            }
            
            // Try each key in order until one works
            const bucket = fullDoc?.bucket || 'innoworx-fdoc-permanent';
            let presignedUrl = null;
            
            for (const s3Key of availableKeys) {
              try {
                // Check if it's already a URL
                if (s3Key.startsWith('http://') || s3Key.startsWith('https://')) {
                  console.log('QuotationView: Using direct URL:', s3Key);
                  setDownloadUrl(s3Key);
                  setDocumentContentType(fullDoc?.content_type || fullDoc?.file_content_type || 'application/pdf');
                  return;
                }
                
                // Convert S3 key to S3 path format
                const s3Path = `s3://${bucket}/${s3Key}`;
                console.log('QuotationView: Trying S3 path:', s3Path);
                
                // Get presigned URL from S3 path
                presignedUrl = await userService.getS3PresignedUrl(s3Path);
                if (presignedUrl) {
                  // Check if URL has wrong region (this will cause 400/403 errors)
                  if (presignedUrl.includes('ap-south-1')) {
                    console.error(
                      '❌ CRITICAL: Presigned URL region mismatch.\n' +
                      'The S3 bucket (innoworx-fdoc-permanent) is in us-east-1, but the presigned URL is generated with ap-south-1.\n' +
                      'This will cause 400/403 errors. Backend fix required: The /s3-presigned endpoint must detect the bucket region and generate URLs with us-east-1.'
                    );
                  }
                  console.log('QuotationView: Successfully got presigned URL for key:', s3Key);
                  setDownloadUrl(presignedUrl);
                  setDocumentContentType(fullDoc?.content_type || fullDoc?.file_content_type || fullDoc?.file_type ? `application/${fullDoc.file_type}` : 'application/pdf');
                  return; // Success, exit loop
                }
              } catch (error) {
                console.warn(`QuotationView: Failed to get presigned URL for key "${s3Key}":`, error);
                // Continue to next key
                continue;
              }
            }
            
            // If we get here, all keys failed
            console.error('QuotationView: All S3 keys failed to generate presigned URLs');
          }
          
          // Do NOT set quotation details for FDOC documents to hide the preview
        } catch (err) {
          console.error('Error fetching FDOC document:', err);
        } finally {
          setIsLoadingDetails(false);
        }
      };

      fetchFdocDocument();
      return;
    }

    // For non-FDOC quotations, fetch details normally
    // Check if we already have the details
    if (quotationDetails[selectedCard.id] && quotationDetails[selectedCard.id].lineItems?.length > 0) {
      return;
    }

    let isMounted = true;

    const fetchQuotationDetails = async () => {
      try {
        const result = await quotationApi.getQuotation(selectedCard.id);
        if (result.success && isMounted) {
          const mapped = mapQuotationToUI(result.quotation);
          setQuotationDetails(prev => ({
            ...prev,
            [selectedCard.id]: mapped,
          }));
          setSelectedCard(mapped);
        }
      } catch (err) {
        console.error('Error fetching quotation details:', err);
      }
    };

    fetchQuotationDetails();

    return () => {
      isMounted = false;
    };
  }, [selectedCard?.id, selectedCard?.fdoc_id, fetchFromFdoc]);

  // Filter quotations by site, month, and quotation number
  const filteredQuotations = useMemo(() => {
    if (!quotationNumberFilter.trim()) {
      return quotations;
    }

    const filterLower = quotationNumberFilter.toLowerCase().trim();
    return quotations.filter(quotation => {
      const quotationNumber = quotation.quotationNumber || '';
      return quotationNumber.toLowerCase().includes(filterLower);
    });
  }, [quotations, quotationNumberFilter]);

  const formatCurrency = (amount = 0) =>
    Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2,
    }).format(amount);

  const handleDownload = (quotation) => {
    if (!quotation) return;

    try {
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 15;
      let yPosition = margin;
      const lineHeight = 7;
      const sectionSpacing = 5;

      const centerText = (text, fontSize = 12, isBold = false) => {
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
        const textWidth = pdf.getTextWidth(text);
        const xPosition = (pageWidth - textWidth) / 2;
        pdf.text(text, xPosition, yPosition);
        yPosition += lineHeight;
      };

      const addText = (text, fontSize = 10, isBold = false) => {
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', isBold ? 'bold' : 'normal');
        pdf.text(text, margin, yPosition);
        yPosition += lineHeight;
      };

      const addTwoColumn = (leftText, rightText, fontSize = 10, leftBold = false, rightBold = false) => {
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', leftBold ? 'bold' : 'normal');
        pdf.text(leftText, margin, yPosition);
        
        pdf.setFont('helvetica', rightBold ? 'bold' : 'normal');
        const rightTextWidth = pdf.getTextWidth(rightText);
        pdf.text(rightText, pageWidth - margin - rightTextWidth, yPosition);
        yPosition += lineHeight;
      };

      const drawLine = () => {
        pdf.line(margin, yPosition, pageWidth - margin, yPosition);
        yPosition += sectionSpacing;
      };

      // Header
      centerText('ESTIMATION / QUOTATION', 18, true);
      yPosition += 3;
      if (quotation.siteName) {
        centerText(quotation.siteName, 12, true);
        yPosition += 2;
      }
      drawLine();
      yPosition += sectionSpacing;

      // Quotation Information
      addText('QUOTATION INFORMATION', 12, true);
      yPosition += 2;
      addTwoColumn('Quotation Number:', quotation.quotationNumber || 'N/A', 10);
      addTwoColumn('Date:', quotation.date || 'N/A', 10);
      if (quotation.expiryDate && quotation.expiryDate !== 'N/A') {
        addTwoColumn('Expiry Date:', quotation.expiryDate, 10);
      }
      if (quotation.siteName) {
        addTwoColumn('Site:', quotation.siteName, 10);
      }
      addTwoColumn('Status:', quotation.status || 'N/A', 10);
      yPosition += sectionSpacing;
      drawLine();
      yPosition += sectionSpacing;

      // Client Information
      if (quotation.client) {
        addText('CLIENT INFORMATION', 12, true);
        yPosition += 2;
        addTwoColumn('Client:', quotation.client, 10);
        yPosition += sectionSpacing;
        drawLine();
        yPosition += sectionSpacing;
      }

      // Items Header
      addText('LINE ITEMS', 12, true);
      yPosition += 3;

      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.text('S.No', margin, yPosition);
      pdf.text('Description', margin + 20, yPosition);
      pdf.text('Qty', margin + 90, yPosition);
      pdf.text('Units', margin + 105, yPosition);
      pdf.text('Rate', margin + 125, yPosition);
      pdf.text('Amount', pageWidth - margin - 30, yPosition, { align: 'right' });
      yPosition += lineHeight;
      drawLine();
      yPosition += 2;

      // Items List
      if (quotation.lineItems && quotation.lineItems.length > 0) {
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        quotation.lineItems.forEach((item, index) => {
          if (yPosition > pageHeight - 40) {
            pdf.addPage();
            yPosition = margin;
          }

          const itemName = item.name || `Item ${index + 1}`;
          const itemLines = pdf.splitTextToSize(itemName, 60);
          const itemHeight = itemLines.length * 5;

          pdf.text((index + 1).toString(), margin, yPosition);
          pdf.text(itemLines, margin + 20, yPosition);
          pdf.text((item.quantity || 0).toString(), margin + 90, yPosition);
          pdf.text(item.units || 'N/A', margin + 105, yPosition);
          pdf.text(formatCurrency(item.rate || 0), margin + 125, yPosition);
          pdf.text(formatCurrency(item.amount || 0), pageWidth - margin, yPosition, { align: 'right' });
          yPosition += Math.max(itemHeight, lineHeight) + 2;
        });
      } else {
        pdf.setFontSize(9);
        pdf.text('No line items found', margin, yPosition);
        yPosition += lineHeight;
      }

      yPosition += sectionSpacing;
      drawLine();
      yPosition += sectionSpacing;

      if (yPosition > pageHeight - 60) {
        pdf.addPage();
        yPosition = margin;
      }

      // Summary
      addText('SUMMARY', 12, true);
      yPosition += 2;
      addTwoColumn('Subtotal:', formatCurrency(quotation.subtotal || 0), 10);
      if (quotation.discount > 0) {
        addTwoColumn('Discount:', formatCurrency(quotation.discount || 0), 10);
      }
      if (quotation.shippingHandling > 0) {
        addTwoColumn('Shipping & Handling:', formatCurrency(quotation.shippingHandling || 0), 10);
      }
      if (quotation.insurance > 0) {
        addTwoColumn('Insurance:', formatCurrency(quotation.insurance || 0), 10);
      }
      if (quotation.totalTax > 0) {
        addTwoColumn('Tax:', formatCurrency(quotation.totalTax || 0), 10);
      }
      yPosition += sectionSpacing;
      drawLine();
      yPosition += sectionSpacing;
      addTwoColumn('GRAND TOTAL:', formatCurrency(quotation.grandTotal || 0), 14, true, true);
      yPosition += sectionSpacing;
      drawLine();
      yPosition += sectionSpacing * 2;

      if (quotation.notes) {
        addText('NOTES', 12, true);
        yPosition += 2;
        const noteLines = pdf.splitTextToSize(quotation.notes, pageWidth - (margin * 2));
        pdf.setFontSize(9);
        noteLines.forEach(line => {
          pdf.text(line, margin, yPosition);
          yPosition += lineHeight - 1;
        });
        yPosition += sectionSpacing;
      }

      centerText('Thank you for your interest!', 10);

      pdf.save(`quotation-${quotation.quotationNumber || 'unknown'}-${quotation.date || 'unknown'}.pdf`);
    } catch (error) {
      console.error('Failed to generate quotation PDF', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  // Animation variants
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.2 } },
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Accepted':
        return 'bg-green-100 text-green-700 border-green-200';
      case 'Pending':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'Rejected':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  return (
    <div className="w-full min-h-screen">
      <div className="mx-auto h-auto">
        <motion.div
          variants={cardVariants}
          initial="hidden"
          animate="visible"
          className="space-y-8"
        >
          <div className="rounded-3xl">
            {/* Quotation Number Filter - Always visible, Site and Month Selection - Only show if not hidden */}
            <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between mb-6">
              <div className="min-w-[200px] flex-1">
                <label htmlFor="quotation-number-filter" className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700">
                  <Search className="w-4 h-4" />
                  Filter by Quotation Number
                </label>
                <input
                  id="quotation-number-filter"
                  type="text"
                  placeholder="Enter Quotation Number or Quotation ID..."
                  value={quotationNumberFilter}
                  onChange={(event) => setQuotationNumberFilter(event.target.value)}
                  className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                />
              </div>

              {!hideSelectors && (
                <>
                <div className="min-w-[200px]">
                  <label htmlFor="quotation-site" className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700">
                    <MapPin className="w-4 h-4" />
                    Select Site
                  </label>
                  <select
                    id="quotation-site"
                    value={selectedSite ? resolveSiteValue(selectedSite) : ''}
                    onChange={(e) => {
                      const siteId = e.target.value;
                      const site = sites.find(s => resolveSiteValue(s) === siteId);
                      setSelectedSite(site || null);
                    }}
                    className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                    required
                    disabled={isSitesLoading}
                  >
                    <option value="">Select a site</option>
                    {sites.map((site) => (
                      <option key={resolveSiteValue(site)} value={resolveSiteValue(site)}>
                        {site.site_name || site.name || 'Unknown Site'}
                      </option>
                    ))}
                  </select>
                  {sitesError && (
                    <p className="mt-1 text-xs text-red-600">{sitesError}</p>
                  )}
                </div>

                <div className="min-w-[200px]">
                  <label htmlFor="quotation-month" className="inline-flex items-center gap-2 mb-2 text-sm font-semibold text-gray-700">
                    <Calendar className="w-4 h-4" />
                    Select Month
                  </label>
                  <input
                    id="quotation-month"
                    type="month"
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    className="w-full min-h-[48px] rounded-2xl border border-gray-200 bg-white px-4 py-2 text-sm text-gray-700 focus:outline-none focus:border-green-500 focus:ring-2 focus:ring-green-100"
                  />
                </div>
                </>
              )}
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-800 font-semibold">Error</p>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Quotations Table */}
            {isLoading ? (
              <div className="flex justify-center items-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
              </div>
            ) : filteredQuotations.length === 0 ? (
              <Card.Light className="p-8 text-center">
                <p className="text-gray-600">No quotations found for the selected site and month.</p>
              </Card.Light>
            ) : (
              <div className="overflow-x-auto rounded-3xl border border-gray-200 bg-white shadow-sm">
                <table className="min-w-full border-collapse text-sm md:text-base">
                  <thead>
                    <tr className="bg-gray-50 text-left text-gray-600">
                      <th className="px-4 py-3 font-semibold">S.No</th>
                      <th className="px-4 py-3 font-semibold min-w-[200px]">Quotation#</th>
                      <th className="px-4 py-3 font-semibold min-w-[140px]">Date</th>
                      <th className="px-4 py-3 font-semibold min-w-[240px]">Client</th>
                      <th className="px-4 py-3 font-semibold min-w-[180px]">Site</th>
                      <th className="px-4 py-3 font-semibold min-w-[100px]">Version</th>
                      <th className="px-4 py-3 font-semibold min-w-[160px]">Total</th>
                      <th className="px-4 py-3 font-semibold min-w-[140px]">Status</th>
                      <th className="px-4 py-3 font-semibold min-w-[200px]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredQuotations.map((quotation, index) => {
                      const rowVersion = quotation.version_no ?? quotation.versionNo;
                      return (
                      <tr
                        key={quotation.id || `quote-row-${index}`}
                        className="border-t border-gray-100 hover:bg-gray-50/60 cursor-pointer"
                        onClick={() => setSelectedCard(quotation)}
                      >
                        <td className="px-4 py-4 align-top text-gray-800">{index + 1}</td>
                        <td className="px-4 py-4 align-top text-gray-800">{quotation.quotationNumber || '—'}</td>
                        <td className="px-4 py-4 align-top text-gray-700">
                          {quotation.date
                            ? new Date(quotation.date).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: '2-digit',
                              })
                            : '—'}
                        </td>
                        <td className="px-4 py-4 align-top text-gray-700">
                          <div className="max-w-[340px] truncate" title={quotation.client || ''}>
                            {quotation.client || '—'}
                          </div>
                        </td>
                        <td className="px-4 py-4 align-top text-gray-700">
                          <div className="max-w-[260px] truncate" title={quotation.siteName || ''}>
                            {quotation.siteName || '—'}
                          </div>
                        </td>
                        <td className="px-4 py-4 align-top text-gray-800">
                          {rowVersion != null && rowVersion !== '' ? `v${rowVersion}` : '—'}
                        </td>
                        <td className="px-4 py-4 align-top text-gray-800">
                          ₹{(quotation.grandTotal ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                        <td className="px-4 py-4 align-top">
                          <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${getStatusColor(quotation.status)}`}>
                            {quotation.status || '—'}
                          </span>
                        </td>
                        <td className="px-4 py-4 align-top">
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDownload(quotation);
                              }}
                              className="inline-flex items-center gap-2 rounded-lg bg-blue-700 px-3 py-1.5 text-xs font-semibold text-white hover:bg-blue-800"
                              title="Download"
                            >
                              <Download className="h-4 w-4 text-white stroke-white" strokeWidth={2} />
                              Download
                            </button>
                          </div>
                        </td>
                      </tr>
                    )})}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </motion.div>
      </div>

      {/* Detail Modal */}
      {selectedCard &&
        createPortal(
          <AnimatePresence>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-[9999] flex flex-col bg-black/70 min-h-screen w-full"
            >
              <div className="flex-shrink-0 px-4 pt-6 pb-4">
                <div className="mx-auto flex w-full max-w-5xl flex-wrap items-center justify-between gap-3">
                  <div className="text-sm font-semibold uppercase tracking-widest text-white/80">
                    {(selectedCard?.fdoc_id || fetchFromFdoc) ? 'Document' : 'Quotation Details'}
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedCard(null);
                        setDownloadUrl(null);
                        setDownloadUrlExpiresIn(null);
                        setDocumentContentType(null);
                      }}
                      className="rounded-full border border-white/40 bg-white/15 px-5 py-2 text-sm font-semibold text-white transition hover:bg-white/25"
                    >
                      Close
                    </button>
                    {!(selectedCard?.fdoc_id || fetchFromFdoc) && (
                      <button
                        type="button"
                        onClick={() => handleDownload(selectedCard)}
                        className="rounded-full bg-emerald-500 px-6 py-2 text-sm font-semibold text-white shadow-lg transition hover:bg-emerald-600"
                      >
                        <span className="inline-flex items-center gap-2">
                          <Download className="w-4 h-4 text-white stroke-white" strokeWidth={2} />
                          Download PDF
                        </span>
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex-1 overflow-y-auto px-4 pb-6">
                <motion.div
                  variants={modalVariants}
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  className="mx-auto w-full max-w-5xl rounded-[32px] bg-white p-6 text-gray-800 shadow-2xl sm:p-8"
                >
                  {isLoadingDetails ? (
                    <div className="text-center py-12">
                      <p className="text-gray-500">Loading...</p>
                    </div>
                  ) : (
                    <>
                      {/* For OO (FDOC) estimations: Only show PDF document, no preview */}
                      {(selectedCard?.fdoc_id || fetchFromFdoc) ? (
                        <>
                          {downloadUrl ? (
                            <div className="w-full h-full min-h-[80vh]">
                              {documentContentType && documentContentType.startsWith('image/') ? (
                                // Display images using img tag
                                <div className="flex justify-center items-center w-full h-full min-h-[80vh] p-4">
                                  <img
                                    src={downloadUrl}
                                    alt="Estimation Document"
                                    className="max-w-full max-h-full object-contain rounded-lg"
                                  />
                                </div>
                              ) : (
                                // Display PDFs using iframe - full screen
                                <div className="w-full h-full min-h-[80vh]">
                                  <iframe
                                    src={downloadUrl}
                                    title="Estimation Document"
                                    className="w-full h-full min-h-[80vh] border-0"
                                    onError={() => {
                                      console.error('Failed to load PDF document. URL may be invalid or expired.');
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="text-center py-12">
                              <div className="max-w-md mx-auto">
                                <p className="text-gray-500 mb-2">No document available or failed to load.</p>
                                {downloadUrl && (
                                  <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                                    <p className="text-sm font-semibold text-yellow-800 mb-1">
                                      Document URL Error
                                    </p>
                                    <p className="text-xs text-yellow-700">
                                      The document URL could not be loaded. This is likely due to a backend configuration issue where presigned URLs are generated with the wrong AWS region (ap-south-1 instead of us-east-1).
                                    </p>
                                    <p className="text-xs text-yellow-700 mt-2">
                                      Please contact support or check backend logs for region mismatch errors.
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          {/* For non-OO estimations: Show quotation details, no document viewer */}
                          <div className="mb-6">
                            <h2 className="text-2xl font-semibold text-gray-900">
                              {selectedCard.quotationNumber}
                            </h2>
                          </div>

                          <div className="space-y-6">
                            {/* Basic Information */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div>
                                <label className="text-sm font-semibold text-gray-600">Quotation Number</label>
                                <p className="text-base text-gray-900">{selectedCard.quotationNumber}</p>
                              </div>
                              <div>
                                <label className="text-sm font-semibold text-gray-600">Date</label>
                                <p className="text-base text-gray-900">
                                  {new Date(selectedCard.date).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric',
                                  })}
                                </p>
                              </div>
                              <div>
                                <label className="text-sm font-semibold text-gray-600">Client</label>
                                <p className="text-base text-gray-900">{selectedCard.client}</p>
                              </div>
                              <div>
                                <label className="text-sm font-semibold text-gray-600">Site</label>
                                <p className="text-base text-gray-900">{selectedCard.siteName}</p>
                              </div>
                              <div>
                                <label className="text-sm font-semibold text-gray-600">Status</label>
                                <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(selectedCard.status)}`}>
                                  {selectedCard.status}
                                </span>
                              </div>
                              {selectedCard.expiryDate && selectedCard.expiryDate !== 'N/A' && (
                                <div>
                                  <label className="text-sm font-semibold text-gray-600">Expiry Date</label>
                                  <p className="text-base text-gray-900">
                                    {new Date(selectedCard.expiryDate).toLocaleDateString('en-US', {
                                      year: 'numeric',
                                      month: 'long',
                                      day: 'numeric',
                                    })}
                                  </p>
                                </div>
                              )}
                            </div>

                            {/* Line Items */}
                            {selectedCard.lineItems && selectedCard.lineItems.length > 0 && (
                              <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Line Items</h3>
                                <div className="overflow-x-auto">
                                  <table className="min-w-full border-collapse">
                                    <thead className="bg-gray-50">
                                      <tr>
                                        <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Item</th>
                                        <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">SAC / HSN</th>
                                        <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Quantity</th>
                                        <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Units</th>
                                        <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700">Rate</th>
                                        <th className="px-4 py-2 text-right text-sm font-semibold text-gray-700">Amount</th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                      {selectedCard.lineItems.map((item, index) => (
                                        <tr key={index}>
                                          <td className="px-4 py-2 text-sm text-gray-900">{item.name}</td>
                                          <td className="px-4 py-2 text-sm text-gray-900">{item.hsnSacCode || item.sacCode || '—'}</td>
                                          <td className="px-4 py-2 text-sm text-gray-900">{item.quantity}</td>
                                          <td className="px-4 py-2 text-sm text-gray-900">{item.units}</td>
                                          <td className="px-4 py-2 text-sm text-gray-900">
                                            ₹{item.rate.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                          </td>
                                          <td className="px-4 py-2 text-sm text-gray-900 text-right">
                                            ₹{item.amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            )}

                            {/* Summary */}
                            <div className="border-t border-gray-200 pt-4">
                              <div className="space-y-2">
                                <div className="flex justify-between text-sm">
                                  <span className="text-gray-600">Subtotal:</span>
                                  <span className="font-medium text-gray-900">
                                    ₹{selectedCard.subtotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </span>
                                </div>
                                {selectedCard.discount > 0 && (
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">Discount:</span>
                                    <span className="font-medium text-gray-900">
                                      -₹{selectedCard.discount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                  </div>
                                )}
                                {selectedCard.shippingHandling > 0 && (
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">Shipping & Handling:</span>
                                    <span className="font-medium text-gray-900">
                                      ₹{selectedCard.shippingHandling.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                  </div>
                                )}
                                {selectedCard.totalTax > 0 && (
                                  <div className="flex justify-between text-sm">
                                    <span className="text-gray-600">Tax:</span>
                                    <span className="font-medium text-gray-900">
                                      ₹{selectedCard.totalTax.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                  </div>
                                )}
                                <div className="flex justify-between text-lg font-bold border-t border-gray-200 pt-2">
                                  <span className="text-gray-900">Grand Total:</span>
                                  <span className="text-green-600">
                                    ₹{selectedCard.grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </span>
                                </div>
                              </div>
                            </div>

                            {selectedCard.notes && (
                              <div>
                                <label className="text-sm font-semibold text-gray-600">Notes</label>
                                <p className="text-base text-gray-900 mt-1">{selectedCard.notes}</p>
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </>
                  )}
                </motion.div>
              </div>
            </motion.div>
          </AnimatePresence>,
          document.body
        )}
    </div>
  );
};

export default QuotationView;

